import { useState } from 'react'
import { useNow } from '@/hooks/useNow'
import { Link } from '@tanstack/react-router'
import { useDecidedSuggestions, useRecentOps, useRejectedOps, useTasks } from '@/db/hooks'
import { retryOp } from '@/lib/actions'
import { fmtMinutes, todayLocal } from '@/lib/time'
import { Button, Empty, PageTitle, Tag } from '@/components/ui'
import { Phrases } from '@/components/Phrases'
import { AppTabs, HISTORY_TABS } from '@/components/AppTabs'

type Preset = 'today' | 'week' | 'all'

/** What got done, never what didn't. The only place with numbers. */
export const LogView = () => {
  const [preset, setPreset] = useState<Preset>('today')
  const tasks = useTasks() ?? []
  const rejected = useRejectedOps()
  const ops = useRecentOps(60)
  const accepted = useDecidedSuggestions()
    .sort((a, b) => ((a.status.decided_at ?? '') < (b.status.decided_at ?? '') ? 1 : -1))
    .slice(0, 20)
  const today = todayLocal()
  const now = useNow()
  const weekAgo = new Date(now - 7 * 86_400_000).toISOString()
  const done = tasks
    .filter((t) => t.status.state === 'done' && t.status.completed_at)
    .filter((t) =>
      preset === 'today'
        ? t.status.completed_at!.slice(0, 10) === today ||
          new Date(t.status.completed_at!).toLocaleDateString('sv') === today
        : preset === 'week'
          ? t.status.completed_at! >= weekAgo
          : true,
    )
    .sort((a, b) => (a.status.completed_at! < b.status.completed_at! ? 1 : -1))
  const minutes = done.reduce((s, t) => s + (t.status.actual_minutes ?? 0), 0)
  return (
    <div>
      <PageTitle>History</PageTitle>
      <AppTabs tabs={HISTORY_TABS} label="History" />
      {rejected.length > 0 && (
        <section className="mb-5 rounded-lg border border-rose-900 bg-rose-950/30 p-3">
          <h2 className="text-sm font-medium text-rose-200 mb-2">The server couldn't apply these</h2>
          <ul className="flex flex-col gap-2">
            {rejected.map((o) => (
              <li key={o.op_id} className="text-sm">
                <div>{o.summary}</div>
                <div className="text-xs text-rose-300/80">{o.error?.message}</div>
                <div className="flex gap-2 mt-1">
                  <Button variant="chip" onClick={() => void retryOp(o.op_id, false)}>
                    Retry
                  </Button>
                  <Button variant="chip" onClick={() => void retryOp(o.op_id, true)}>
                    Discard
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
      <div className="flex gap-1.5 mb-3">
        {(['today', 'week', 'all'] as Preset[]).map((p) => (
          <Button key={p} variant={preset === p ? 'chipOn' : 'chip'} onClick={() => setPreset(p)}>
            {p === 'today' ? 'Done today' : p === 'week' ? 'Done this week' : 'All done'}
          </Button>
        ))}
      </div>
      {/* the Log is the one place that counts, and it counts what got done */}
      <Phrases
        as="p"
        className="flex text-sm text-muted mb-3"
        data-testid="log-count"
        parts={[
          <>
            <strong className="text-fg text-lg tabular-nums">{done.length}</strong> finished
          </>,
          !!minutes && `${fmtMinutes(minutes)} tracked`,
        ]}
      />
      {done.length === 0 ? (
        <Empty>Nothing finished yet{preset === 'today' ? ' today' : ''}. That's fine.</Empty>
      ) : (
        <ul className="group-list">
          {done.map((t) => (
            <li key={t.id} className="flex items-center gap-2.5 text-sm px-3.5 py-2.5 min-h-11">
              <span className="text-emerald-400">✓</span>
              <Link to="/tasks/$id" params={{ id: t.id }} className="flex-1 truncate">
                {t.spec.title}
              </Link>
              {t.status.actual_minutes > 0 && <Tag>{fmtMinutes(t.status.actual_minutes)}</Tag>}
              <span className="text-xs text-faint tabular-nums">
                {new Date(t.status.completed_at!).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </li>
          ))}
        </ul>
      )}
      {accepted.length > 0 && (
        <section className="mt-8">
          <h2 className="text-[13px] font-semibold text-muted mb-2">Suggestions you accepted</h2>
          <ul className="text-sm text-muted space-y-1">
            {accepted.map((s) => (
              <li key={s.id}>✨ {s.spec.title}</li>
            ))}
          </ul>
        </section>
      )}
      {ops.length > 0 && (
        <section className="mt-8">
          <h2 className="text-[13px] font-semibold text-muted mb-2">Recent activity on this device</h2>
          <ul className="text-xs text-faint space-y-1">
            {ops.slice(0, 30).map((o) => (
              <li key={o.op_id} className="flex justify-between gap-2">
                <span className="truncate">{o.summary}</span>
                <span className="shrink-0">{o.state === 'acked' ? '' : o.state}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
